import { audioCtx, musicBus } from './audio-engine';
/* ============================================================
   音軌播放 —— 使用者提供的音樂檔（2026-08-06）
   ============================================================

   使用者把兩首曲子放進 `assets/music/`（各約 30 MB 的 WAV），
   轉碼後放在 `public/music/`：

   | 音軌 | 檔案 | 用在哪 | 長度 |
   |---|---|---|---|
   | `main` | `main-theme` | 標題／機庫／圖鑑／設定 | 2:48 |
   | `level` | `level-1` | 對局中 | 2:53 |

   兩種格式各一份，`<source>` 讓瀏覽器自己挑：
   **WebM/Opus 96k**（2.0–2.3 MB，主要）與 **MP3 128k**（2.6 MB，Safari 16 以下）。
   原始 WAV 共 59 MB —— 那個大小對 web 遊戲是不能上線的載入量。

   ## 為什麼用 `<audio>` 而不是 `decodeAudioData`

   `decodeAudioData` 會把整首解成 PCM 放在記憶體：兩首各 3 分鐘立體聲
   ＝ 約 60 MB RAM，而且要**全部下載完**才能開始播。
   `<audio>` 是串流的：邊下載邊播，記憶體只有緩衝區。
   代價是循環點可能有極短的間隙（MP3 的編碼器 padding），
   對 3 分鐘的氛圍曲可以接受 —— 真的礙耳再改成雙元素交叉淡入。

   ## 自動播放

   瀏覽器要求一次使用者手勢才能出聲。標題畫面**在任何手勢之前就顯示了**，
   所以 `play()` 會被擋 —— 這裡記下待播的音軌，等第一次
   pointerdown／keydown 再補播（`armGesture`）。 */

export type TrackId = 'main' | 'level';

var SRC: Record<TrackId, string> = {
  main: '/music/main-theme',
  level: '/music/level-1'
};

type Deck = { el: HTMLAudioElement; g: any; src: any };
var decks: Partial<Record<TrackId, Deck>> = {};
var curId: TrackId | null = null;
var pending: TrackId | null = null;
var armed = false;

/** 淡入淡出的秒數。音樂切換一定要淡 —— 硬切在標題→出擊之間會像斷電。 */
var FADE = 1.1;

function deck(id: TrackId): Deck | null {
  var ctx = audioCtx(), bus = musicBus();
  if (!ctx || !bus) return null;
  var d = decks[id];
  if (d) return d;
  var el = document.createElement('audio');
  el.loop = true;
  el.preload = 'auto';
  el.crossOrigin = 'anonymous';
  /* 兩個 `<source>`，瀏覽器挑第一個支援的。順序有意義：Opus 檔案小又不吃
     MP3 的編碼延遲，所以排前面。 */
  ['webm', 'mp3'].forEach(function (ext) {
    var s = document.createElement('source');
    s.src = SRC[id] + '.' + ext;
    s.type = ext === 'webm' ? 'audio/webm; codecs=opus' : 'audio/mpeg';
    el.appendChild(s);
  });
  var g = ctx.createGain();
  g.gain.value = 0.0001;
  /* `createMediaElementSource` 對同一個 element **只能建立一次** ——
     建第二次會拋例外，而且症狀是「這首曲子從此靜音」。所以 deck 要快取。 */
  var src = ctx.createMediaElementSource(el);
  src.connect(g); g.connect(bus);
  /* **掛進 DOM**。播放本身不需要（音訊走 MediaElementSource），但游離的
     `<audio>` 在行動瀏覽器的省電策略下會被暫停或回收，而且 devtools 看不到它
     —— 那會讓「沒聲音」變成一個沒有現場可查的問題。 */
  el.style.display = 'none';
  document.body.appendChild(el);
  d = { el: el, g: g, src: src };
  decks[id] = d;
  return d;
}

/** Debug：每個 deck 的實際狀態。音樂的失效模式全是安靜的，沒有這個只能猜。 */
export function trackDebug() {
  return (['main', 'level'] as TrackId[]).map(function (id) {
    var d = decks[id];
    if (!d) return { id: id, built: false };
    return {
      id: id, built: true,
      src: (d.el.currentSrc || '').split('/').pop(),
      paused: d.el.paused,
      t: +d.el.currentTime.toFixed(2),
      gain: +d.g.gain.value.toFixed(3),
      ready: d.el.readyState        // 4 = 可完整播放
    };
  });
}

/** 第一次使用者手勢時補播被擋下的音軌。掛一次就好。 */
function armGesture() {
  if (armed) return;
  armed = true;
  var go = function () {
    var ctx = audioCtx();
    if (ctx && ctx.state === 'suspended') ctx.resume();
    if (pending) { var p = pending; pending = null; playTrack(p); }
  };
  window.addEventListener('pointerdown', go, { once: true });
  window.addEventListener('keydown', go, { once: true });
}

/** 切到某個音軌（`null` ＝ 全部淡出）。同一個音軌重複呼叫是安全的 no-op。 */
export function playTrack(id: TrackId | null) {
  var ctx = audioCtx();
  if (!ctx) return false;
  if (id === curId) return true;
  var now = ctx.currentTime;

  // 舊的淡出（不 pause —— 淡出還沒走完就 pause 會直接斷掉）
  if (curId && decks[curId]) {
    var old = decks[curId]!;
    old.g.gain.cancelScheduledValues(now);
    old.g.gain.setValueAtTime(Math.max(0.0001, old.g.gain.value), now);
    old.g.gain.linearRampToValueAtTime(0.0001, now + FADE);
    (function (o) { setTimeout(function () { if (o.g.gain.value < 0.01) o.el.pause(); }, FADE * 1000 + 60); })(old);
  }
  curId = id;
  if (!id) return true;

  var d = deck(id);
  if (!d) return false;
  /* context 是 suspended 時，`el.play()` 會成功但**聽不到任何聲音**
     （音訊走 MediaElementSource 進了一個沒在跑的圖）。這一條最難查：
     元素在播、currentTime 在動、沒有錯誤，就是沒聲音。 */
  if (ctx.state === 'suspended') armGesture();
  d.g.gain.cancelScheduledValues(now);
  d.g.gain.setValueAtTime(Math.max(0.0001, d.g.gain.value), now);
  d.g.gain.linearRampToValueAtTime(1, now + FADE);
  var pr = d.el.play();
  if (pr && pr.catch) pr.catch(function () {
    /* 被自動播放政策擋下。記下來等手勢 —— **不要**當成錯誤，
       標題畫面在任何點擊之前顯示是正常流程。 */
    pending = id; curId = null; armGesture();
  });
  return true;
}

export function currentTrack() { return curId; }
export function trackPending() { return pending; }
